import React from "react";
import { useCart, useSettings } from "hooks/useStore";
import Money from "components/money";
import clsx from "clsx/lite";

const getProgress = (current, min, max) =>
	Math.min(((current - min) / (max - min)) * 100, 100);

const Message = ({ nextTier, spendMore, maxDiscount, currencyCode }) => (
	<p className={clsx("font-medium", nextTier ? "text-sm md:text-lg" : "text-lg")}>
		{nextTier ? (
			<>
				Spend{" "}
				<span className="font-bold">
					<Money value={spendMore} currencyCode={currencyCode} omitCurrencyCode />
				</span>{" "}
				more to unlock your special savings!
			</>
		) : (
			<p className="text-sm md:text-base">{`You've unlocked the max discount: ${maxDiscount}% 🎉`}</p>
		)}
	</p>
);

const Badge = ({ isAchieved, index, code }) => {
	if (index === 0 && isAchieved) {
		return (
			<span className="text-sm md:text-base py-1 px-2 border border-yellow bg-seashell md:-mr-12 leading-none flex whitespace-nowrap">
				<span className="hidden md:flex md:mr-1">Use </span> Code: {code}
			</span>
		);
	}
	return (
		<span
			className={clsx(
				"py-1 px-2 border leading-none text-sm md:text-base",
				isAchieved ? "border-yellow bg-seashell md:-mr-2 lg:-mr-6" : "border-[#E8E8E8] lg:-mr-5"
			)}
		>
			{isAchieved ? "Unlocked" : "Locked"}
		</span>
	);
};

const ProgressBar = ({ progress, isAchieved, show }) => (
	<div className="flex items-center">
		<div className="relative h-[5px] bg-gray-300 rounded w-full">
			<div
				className="absolute top-0 left-0 h-full bg-beige rounded transition-all duration-300"
				style={{ width: show ? `${progress}%` : 0 }}
			/>
		</div>
		<div className="w-fit -ml-3 z-10">
			<div className={clsx("size-4 md:size-7 rounded-full", isAchieved ? "bg-beige" : "bg-gray-300")}
			/>
		</div>
	</div>
);

const TierItem = ({ tier, index, currentSpend, prevAmount, code, currencyCode }) => {
	const progress = getProgress(currentSpend, prevAmount, tier.amount);
	const isAchieved = currentSpend >= tier.amount;
	const showProgress = currentSpend >= prevAmount;

	return (
		<div className="w-full">
			<span className="text-sm md:text-base flex justify-end mb-2 mr-1 leading-none">
				<Money value={tier.amount} currencyCode={currencyCode} omitCurrencyCode />
			</span>
			<ProgressBar progress={progress} isAchieved={isAchieved} show={showProgress} />
			<div className="flex flex-col items-end gap-4 md:gap-2 md:pt-2">
				<span
					className={clsx(
						"text-sm md:text-base lg:-mr-5 md:font-bold",
						isAchieved ? "text-deepBeige" : "text-black"
					)}
				>
					{tier.discount}% OFF
				</span>
				<Badge isAchieved={isAchieved} index={index} code={code} />
			</div>
		</div>
	);
};

const DiscountTiers = ({ data, className }) => {
	const { totalPrice } = useCart(["totalPrice"]);
	const { customerCountry, currencyCode } = useSettings(["customerCountry", "currencyCode"]);

	if (!data?.show || !data.thresholds?.length || !totalPrice) return null;

	const sortedTiers = [...data.thresholds]
		.map((tier) => ({ ...tier, amount: tier.amounts[customerCountry] || 0, }))
		.sort((a, b) => a.amount - b.amount);

	const currentSpend = totalPrice;
	const nextTier = sortedTiers.find(t => currentSpend < t.amount);
	const spendMore = nextTier ? nextTier.amount - currentSpend : 0;
	const maxDiscount = sortedTiers.at(-1)?.discount || 0;

	return (
		<div className={className}>
			<div className="px-4 py-5 md:p-3 lg:px-5 bg-cream border border-[#D9D9D9] w-full flex justify-center">
				<div className="max-w-lg w-full">
					<div className="mb-6 md:mb-3.5 flex justify-center text-sm">
						<Message nextTier={nextTier} spendMore={spendMore} maxDiscount={maxDiscount} currencyCode={currencyCode} />
					</div>
					<div className="flex w-full items-center gap-2">
						{sortedTiers.map((tier, idx) => (
							<TierItem key={idx} tier={tier} index={idx} currentSpend={currentSpend}
								prevAmount={idx === 0 ? 0 : sortedTiers[idx - 1].amount}
								code={data.code} currencyCode={currencyCode}
							/>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

export default DiscountTiers;